/**
 * MCP server form component — CoordioCore form-page-pattern.
 *
 * Layout: PageFormLayout (ShellHeader + SectionSideNav grid + panel sections)
 * Fields: @coordio/ui FormField (label + ⓘ tooltip + error + required asterisk)
 * Cards:  @coordio/ui FormSection (title + description + bordered body)
 * Toggle: @coordio/ui Switch in ui-form-choice-field CSS classes
 */

// React Imports
import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Server, Settings, Plus as AddIcon, Trash2 as DeleteIcon } from 'lucide-react';

// UI Imports
import { Button, FormField, Input, Switch, Textarea } from '@coordio/ui';
import { FormSection } from '@coordio/ui/layout';

// Component Imports
import { PageFormLayout } from '../layouts/PageFormLayout';
import { mcpService } from '../../services/mcpService';
import { useNotificationStore } from '../../store/notificationStore';

/**
 * MCP server form component for creating and editing MCP servers.
 */
export const McpServerForm = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const isEdit = !!id;
  const { addNotification } = useNotificationStore();

  const [loading, setLoading] = useState(isEdit);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Form fields
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [serverUrl, setServerUrl] = useState('');
  const [authenticationJson, setAuthenticationJson] = useState('');
  const [isActive, setIsActive] = useState(true);
  const [healthCheckEndpoint, setHealthCheckEndpoint] = useState('');
  const [healthCheckIntervalMinutes, setHealthCheckIntervalMinutes] = useState<number | undefined>(5);
  const [metadataJson, setMetadataJson] = useState('');
  const [headers, setHeaders] = useState<Array<{ id: string; key: string; value: string }>>([]);

  useEffect(() => {
    if (isEdit && id) {
      loadServer();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, isEdit]);

  const loadServer = async () => {
    if (!id) return;
    setLoading(true);
    setError(null);
    try {
      const data = await mcpService.getMcpServer(id);
      setName(data.name);
      setDescription(data.description || '');
      setServerUrl(data.serverUrl);
      setAuthenticationJson('');
      setIsActive(data.isActive);
      setHealthCheckEndpoint(data.healthCheckEndpoint || '');
      setHealthCheckIntervalMinutes(data.healthCheckIntervalMinutes);
      setMetadataJson('');
      if (data.headersJson && data.headersJson.trim()) {
        try {
          const parsed = JSON.parse(data.headersJson) as Record<string, string>;
          setHeaders(
            Object.entries(parsed).map(([k, v], i) => ({ id: `header-${i}-${k}`, key: k, value: v ?? '' }))
          );
        } catch {
          setHeaders([]);
        }
      } else {
        setHeaders([]);
      }
    } catch (err: any) {
      const msg = err.response?.data?.message || 'Failed to load MCP server';
      setError(msg);
      addNotification(msg, 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async () => {
    setError(null);

    if (!name.trim()) {
      setError('Name is required');
      return;
    }

    if (!serverUrl.trim()) {
      setError('Server URL is required');
      return;
    }

    try {
      new URL(serverUrl.trim());
    } catch {
      setError('Server URL must be a valid URL');
      return;
    }

    if (healthCheckEndpoint.trim()) {
      try {
        new URL(healthCheckEndpoint.trim());
      } catch {
        setError('Health check endpoint must be a valid URL');
        return;
      }
    }

    if (authenticationJson.trim()) {
      try {
        JSON.parse(authenticationJson.trim());
      } catch {
        setError('Authentication JSON must be valid JSON');
        return;
      }
    }

    if (metadataJson.trim()) {
      try {
        JSON.parse(metadataJson.trim());
      } catch {
        setError('Metadata JSON must be valid JSON');
        return;
      }
    }

    const headersObj: Record<string, string> = {};
    for (const h of headers) {
      const k = h.key?.trim();
      if (k) headersObj[k] = h.value?.trim() ?? '';
    }
    const headersJson = Object.keys(headersObj).length > 0 ? JSON.stringify(headersObj) : undefined;

    setSaving(true);
    try {
      const data = {
        name: name.trim(),
        description: description.trim() || undefined,
        serverUrl: serverUrl.trim(),
        authenticationJson: authenticationJson.trim() || undefined,
        headersJson,
        isActive,
        healthCheckEndpoint: healthCheckEndpoint.trim() || undefined,
        healthCheckIntervalMinutes: healthCheckIntervalMinutes || undefined,
        metadataJson: metadataJson.trim() || undefined,
      };

      if (isEdit && id) {
        await mcpService.updateMcpServer(id, data);
        addNotification('MCP server updated successfully', 'success');
      } else {
        await mcpService.createMcpServer(data);
        addNotification('MCP server created successfully', 'success');
      }
      navigate('/mcp-servers');
    } catch (err: any) {
      const msg = err.response?.data?.message || 'Failed to save MCP server';
      setError(msg);
      addNotification(msg, 'error');
    } finally {
      setSaving(false);
    }
  };

  return (
    <PageFormLayout
      title={isEdit ? 'Edit MCP Server' : 'Create MCP Server'}
      subtitle="Register and configure Model Context Protocol servers for agent tool access."
      data-testid="mcp-server-form-page"
      loading={loading}
      error={error ?? undefined}
      onErrorDismiss={() => setError(null)}
      backTo={{ label: 'Back to List', href: '/mcp-servers' }}
      actions={{
        onSave: handleSubmit,
        onCancel: () => navigate('/mcp-servers'),
        saveLabel: isEdit ? 'Update' : 'Create',
        saving,
      }}
      navGroups={[
        {
          title: 'Profile',
          items: [
            { id: 'details', label: 'Details', icon: <Server size={16} /> },
          ],
        },
        {
          title: 'Configuration',
          items: [
            { id: 'headers', label: 'Headers', icon: <Settings size={16} /> },
          ],
        },
      ]}
      sections={[
        /* ── Details ─────────────────────────────────────────────── */
        {
          id: 'details',
          title: 'Details',
          description: 'Server name, endpoint URL, authentication, and health check configuration.',
          icon: <Server size={20} />,
          content: (
            <FormSection
              title="Server identity"
              description="Provide the server name, endpoint URL, and optional authentication credentials."
            >
              <div className="grid gap-5 items-end sm:grid-cols-2">
                <FormField
                  label="Name"
                  required
                  description="Unique name for this MCP server registration."
                  data-testid="mcp-server-name"
                >
                  <Input
                    className="w-full"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    required
                    data-testid="mcp-server-name"
                  />
                </FormField>

                <FormField
                  label="Server URL"
                  required
                  description="The MCP server endpoint URL (e.g., https://mcp.example.com)."
                  data-testid="mcp-server-url"
                >
                  <Input
                    className="w-full"
                    value={serverUrl}
                    onChange={(e) => setServerUrl(e.target.value)}
                    required
                    data-testid="mcp-server-url"
                  />
                </FormField>
              </div>

              <div className="mt-5">
                <FormField
                  label="Description"
                  description="Optional context about this MCP server's purpose."
                  data-testid="mcp-server-description"
                >
                  <Textarea
                    className="w-full"
                    rows={3}
                    value={description}
                    onChange={(e) => setDescription(e.target.value)}
                    data-testid="mcp-server-description"
                  />
                </FormField>
              </div>

              <div className="mt-5">
                <FormField
                  label="Authentication JSON"
                  description="JSON configuration for server authentication (optional)."
                  data-testid="mcp-server-authJson"
                >
                  <Textarea
                    className="w-full"
                    rows={4}
                    value={authenticationJson}
                    onChange={(e) => setAuthenticationJson(e.target.value)}
                    data-testid="mcp-server-authJson"
                  />
                </FormField>
              </div>

              <div className="mt-5 grid gap-5 items-end sm:grid-cols-2">
                <FormField
                  label="Health Check Endpoint"
                  description="Optional custom health check endpoint URL."
                  data-testid="mcp-server-healthCheckEndpoint"
                >
                  <Input
                    className="w-full"
                    value={healthCheckEndpoint}
                    onChange={(e) => setHealthCheckEndpoint(e.target.value)}
                    data-testid="mcp-server-healthCheckEndpoint"
                  />
                </FormField>

                <FormField
                  label="Health Check Interval (minutes)"
                  description="Interval between health checks (default: 5 minutes)."
                  data-testid="mcp-server-healthCheckInterval"
                >
                  <Input
                    className="w-full"
                    type="number"
                    value={healthCheckIntervalMinutes || ''}
                    onChange={(e) =>
                      setHealthCheckIntervalMinutes(
                        e.target.value ? parseInt(e.target.value, 10) : undefined
                      )
                    }
                    data-testid="mcp-server-healthCheckInterval"
                  />
                </FormField>
              </div>

              <div className="mt-5">
                <FormField
                  label="Metadata JSON"
                  description="Optional metadata as JSON."
                  data-testid="mcp-server-metadataJson"
                >
                  <Textarea
                    className="w-full"
                    rows={3}
                    value={metadataJson}
                    onChange={(e) => setMetadataJson(e.target.value)}
                    data-testid="mcp-server-metadataJson"
                  />
                </FormField>
              </div>
            </FormSection>
          ),
        },

        /* ── Headers ─────────────────────────────────────────────── */
        {
          id: 'headers',
          title: 'Headers',
          description: 'Custom HTTP headers and activation status.',
          icon: <Settings size={20} />,
          content: (
            <FormSection
              title="Custom headers"
              description="Optional HTTP headers to send with every request to this MCP server (e.g. X-API-Key, Accept)."
            >
              <table className="w-full text-sm" data-testid="mcp-server-headers-table">
                <tbody>
                  {headers.map((row) => (
                    <tr key={row.id}>
                      <td className="w-[40%] p-1">
                        <Input
                          className="w-full"
                          placeholder="Header name"
                          value={row.key}
                          onChange={(e) =>
                            setHeaders((prev) =>
                              prev.map((r) => (r.id === row.id ? { ...r, key: e.target.value } : r))
                            )
                          }
                          data-testid={`mcp-server-header-key-${row.id}`}
                        />
                      </td>
                      <td className="w-[50%] p-1">
                        <Input
                          className="w-full"
                          placeholder="Value"
                          value={row.value}
                          onChange={(e) =>
                            setHeaders((prev) =>
                              prev.map((r) => (r.id === row.id ? { ...r, value: e.target.value } : r))
                            )
                          }
                          data-testid={`mcp-server-header-value-${row.id}`}
                        />
                      </td>
                      <td className="w-12 p-1">
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => setHeaders((prev) => prev.filter((r) => r.id !== row.id))}
                          aria-label="Remove header"
                          type="button"
                          data-testid={`mcp-server-header-remove-${row.id}`}
                        >
                          <DeleteIcon size={16} />
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={() =>
                  setHeaders((prev) => [
                    ...prev,
                    { id: `new-${Date.now()}-${Math.random()}`, key: '', value: '' },
                  ])
                }
                className="mt-2"
                data-testid="mcp-server-add-header"
              >
                <AddIcon size={16} className="mr-1" />
                Add header
              </Button>

              <div className="mt-5">
                <div className="ui-form-choice-field">
                  <div className="ui-form-choice-field__row">
                    <div className="ui-form-choice-field__control">
                      <Switch
                        checked={isActive}
                        onCheckedChange={(checked) => setIsActive(checked)}
                        data-testid="mcp-server-isActive"
                      />
                    </div>
                    <div className="ui-form-choice-field__label-cell">
                      <span className="ui-form-choice-field__label">Active</span>
                    </div>
                  </div>
                </div>
              </div>
            </FormSection>
          ),
        },
      ]}
    />
  );
};
